import {Injectable} from '@angular/core';
import {RequesterService} from '../../../services/requester.service';
import {
    ReviewReportFeedbackFormModel,
    ReviewReportFormModel, ReviewReportOne2OneFormModel,
    ReviewReportSimpleModel,
    UserReviewReportListResponse
} from '../models/review.model';
import {RequesterResponseModel} from '../../../services/models/requester.model';
import {EventsService} from '../../../services/events.service';
import {DelegateSummaryModel} from '../../team/models/delegate.model';

@Injectable({
    providedIn: 'root'
})
export class ReviewService {
    constructor(
        private requesterService: RequesterService,
        private event: EventsService
    ) {
    }

    getReviewQuestionTypes() {
        return ['self', 'manager', 'feedback', 'goal', 'note'];
    }

    getMyReviewReportList(): Promise<ReviewReportSimpleModel[]> {
        return this.requesterService.get('getMyReviewReportList', {})
            .then(data => {
                if (!!data.result) {
                    return data.result;
                }
                return null;
            })
            .catch(e => {
                console.log('Error getMyReviewReportList >> ', e);
            });
    }

    getMyReviewReport(report_id: number): Promise<ReviewReportFormModel> {
        return this.requesterService.get('getMyReviewReport', {report_id, type: 'review'})
            .then(data => {
                if (!!data.result) {
                    return data.result;
                }
                return null;
            })
            .catch(e => {
                console.log('Error getMyReviewReport >> ', e);
                return null;
            });
    }

    getMyReviewReportOne2One(report_id: number): Promise<ReviewReportOne2OneFormModel> {
        return this.requesterService.get('getMyReviewReport', {report_id, type: 'one2one'})
            .then(data => {
                if (!!data.result) {
                    return data.result;
                }
                return null;
            })
            .catch(e => {
                console.log('Error getMyReviewReportOne2One >> ', e);
                return null;
            });
    }

    saveMyReviewReport(report): Promise<ReviewReportFormModel|ReviewReportOne2OneFormModel|RequesterResponseModel> {
        return this.requesterService.post('saveMyReviewReport', report)
            .then(data => {
                if (!!data.result) {
                    this.event.publish('review.saveMyReviewReport');
                    return data.result;
                }
                throw "Response structure is not valid";
            })
            .catch(e => {
                console.log('Error saveMyReviewReport >> ', e);
            });
    }

    deleteMyReviewReport(report_id: number): Promise<RequesterResponseModel> {
        return this.requesterService.post('deleteMyReviewReport', {report_id})
            .then(data => {
                if (!!data.result) {
                    this.event.publish('review.deleteMyReviewReport');
                    return data.result;
                }
                throw "Cannot delete report";
            })
            .catch(e => {
                console.log('Error deleteMyReviewReport >> ', e);
            });
    }

    updateMyReviewReportStatus(report_id: number, status: boolean): Promise<RequesterResponseModel> {
        return this.requesterService.post('updateMyReviewReportStatus', {report_id, status})
            .then(data => {
                if (!!data.result) {
                    this.event.publish('review.updateMyReviewReportStatus');
                    return data.result;
                }
                throw "Response structure is not valid";
            })
            .catch(e => {
                console.log('Error updateMyReviewReportStatus >> ', e);
            });
    }

    updateMyReviewReportFeedbackStatus(feedback_id: number, status: boolean): Promise<RequesterResponseModel> {
        return this.requesterService.post('updateMyReviewReportFeedbackStatus', {feedback_id, status})
            .then(data => {
                if (!!data.result) {
                    this.event.publish('review.updateMyReviewReportFeedbackStatus');
                    return data.result;
                }
                throw "Response structure is not valid";
            })
            .catch(e => {
                console.log('Error updateMyReviewReportFeedbackStatus >> ', e);
            });
    }

    updateMyReviewReportGoalStatus(goal_id: number, status: boolean): Promise<RequesterResponseModel> {
        return this.requesterService.post('updateMyReviewReportGoalStatus', {goal_id, status})
            .then(data => {
                if (!!data.result) {
                    this.event.publish('review.updateMyReviewReportGoalStatus');
                    return data.result;
                }
                throw "Response structure is not valid";
            })
            .catch(e => {
                console.log('Error updateMyReviewReportGoalStatus >> ', e);
            });
    }


    getMyReviewTeamList(managerId: number = 0): Promise<DelegateSummaryModel[]> {
        return this.requesterService.get('getMyReviewTeamList', {manager_id: managerId})
            .then(data => {
                if (!!data.result) {
                    return data.result;
                }
                return null;
            })
            .catch(e => {
                console.log('Error getMyReviewTeamList >> ', e);
                return null;
            });
    }

    getUserReviewReportList(delegate_id): Promise<UserReviewReportListResponse> {
        return this.requesterService.get('getUserReviewReportList', {delegate_id})
            .then(data => {
                if (!!data.result) {
                    return data.result;
                }
                return null;
            })
            .catch(e => {
                console.log('Error getUserReviewReportList >> ', e);
            });
    }


    getReviewReportFeedback(report_id: number): Promise<ReviewReportFeedbackFormModel> {
        return this.requesterService.get('getReviewReportFeedback', {report_id})
            .then(data => {
                if (!!data.result) {
                    return data.result;
                }
                return null;
            })
            .catch(e => {
                console.log('Error getReviewReportFeedback >> ', e);
                return null;
            });
    }

    saveReviewReportFeedback(report: ReviewReportFeedbackFormModel): Promise<ReviewReportFeedbackFormModel|RequesterResponseModel> {
        return this.requesterService.post('saveReviewReportFeedback', report)
            .then(data => {
                if (!!data.result) {
                    this.event.publish('review.saveReviewReportFeedback');
                    return data.result;
                }
                throw "Response structure is not valid";
            })
            .catch(e => {
                console.log('Error saveReviewReportFeedback >> ', e);
            });
    }

    addReviewReportFeedbackRespondent(report_id: number, respondent_id: number): Promise<ReviewReportFeedbackFormModel|RequesterResponseModel> {
        return this.requesterService.post('addReviewReportFeedbackRespondent', {report_id, respondent_id})
            .then(data => {
                if (!!data.result) {
                    return data.result;
                }
                if (!!data.error && !!data.error.errorMessage) {
                    throw data.error.errorMessage;
                }
                throw "Response structure is not valid";
            })
            .catch(e => {
                console.log('Error addReviewReportFeedbackRespondent >> ', e);
                throw e;
            });
    }
}
